import { Component, OnInit, OnDestroy } from "@angular/core";
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, } from 'rxjs';

import { AuthService } from '../../../../services/auth.service';
import { ContactService, ContactSearchSpec } from "../../../services/contacts.service";
import { DomainDefinitionService } from '../../../../builder/services/domain-definition.service';
import { LoaderService } from '../../../../common/services/loader.service';
import { OrganizationService } from '../../../../common/services/organization.service';
import { SubHeaderService } from "../../../../core/services/sub-header.service";

import { AgentType, AgentTypeOptions, Contact, ContactStub } from "../../../models/contact.model";
import { ContactGroup } from '../../../models/contactGroup.model';
import { OrganizationStub } from '../../../../common/models/organization.model';
import { Group } from "../../../models/group.model";
import { LunaDomain } from '../../../../builder/models/domain.model';
import { Option } from "../../../../common/models/flow.dto";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { LunaClaimTypes, LunaClaimValues } from "../../../../models/user-identity.model";
import { GridOptions, ColDef } from "ag-grid-community";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { IconActionRendererComponent } from "../../../../common/components/ag-grid/icon-action-renderer.component";
import { AgGridBooleanFilterComponent, IBooleanFilterParams } from "../../../../common/components/ag-grid/filters/boolean-filter.component";
import { CheckboxRendererComponent } from "../../../../common/components/ag-grid/checkbox-renderer.component";

@Component({
    selector: "contact-deletion",
    templateUrl: "./contact-deletion.component.html",
    styleUrls: ['./contact-deletion.component.scss'],
    standalone: false
})
export class ContactDeletionComponent implements OnInit, OnDestroy {

    public _contact: Contact = null;
    public _contacts: Contact[];

    public nonUserGroups: Group[] = [];
    public organization: OrganizationStub;
    public permissionGp: ContactGroup;
    public permissionTypes: { id: string, name: string, chosen: boolean, active: boolean }[];
    public states: LunaDomain;
    public userGroups: Group[] = [];
    public readOnlyMode: boolean = false;
    public hasAdminManageContactsPermissions: boolean = false;
    public hasLimitedAdminManageContactsPermissions: boolean = false;
    public isDesignatedRepOrEmergencyPOCOrStatutoryAgent: boolean = false;
    public externalPermissionGroups: Group[] = [];
    public agentTypes = new Array<Option>();
    public contactFullName: string = "";
    public gridOptions: GridOptions;

    //This lists both org_type entries, first the codes from their ORG_TYPE domain
    orgTypesRequiringAStatutoryAgent: string[] = ["CO", "LC", "LL", "LP", "TR",
    /*Here are the corresponding codes from our BUSINESS_TYPE DOMAIN*/ "CORP", "LLC", "LLP", "BIZTRUST", "PRSTRUST", "REITRUST"];
    _booleanfilterParams = { isInServerSideFilterMode: false, } as IBooleanFilterParams;
    public subHeader: SubHeader = new SubHeader({ title: "Delete Contact", buttons: [{ name: "Cancel", method: () => this.cancel() }] });
    public addContactButtonName: string = "Add Contact";


    subscriptions: Subscription = new Subscription();
    deleteConfirmDialog: Dialog = new Dialog("PlaceHolder", [new DialogButton("Yes", true, () => this.onDeleteDialogConfirm()), new DialogButton("Cancel", false, () => this.onDeleteDialogClose())]);

    get agentCellClass(): string {
        let cellClass = "cell small-12";
        if (this.agentTypes.length % 6 === 0) {
            cellClass += " large-2 medium-4";
        } else if (this.agentTypes.length % 4 === 0) {
            cellClass += " large-3 medium-6";
        } else {
            cellClass += " large-4 medium-6";
        }
        return cellClass;
    }

    get canCreateUser(): boolean {
        return this.hasAdminManageContactsPermissions || (this.hasLimitedAdminManageContactsPermissions && this.isDesignatedRepOrEmergencyPOCOrStatutoryAgent);
    }

    constructor(
        public readonly authService: AuthService,
        public readonly contactService: ContactService,
        public readonly organizationService: OrganizationService,
        public readonly activatedRoute: ActivatedRoute,
        public location: Location,
        public subHeaderService: SubHeaderService,
        public loader: LoaderService,
        public domainService: DomainDefinitionService,
        public dialogService: DynamicDialogService,
        public gridFormatterService: GridFormatterService,
        public router: Router
    ) {
    }

    ngOnInit(): void {
        const contactId = this.activatedRoute.snapshot.params["id"];
        const orgId = this.activatedRoute.snapshot.params["orgId"];

        this.subHeaderService.updateSubHeader(this.subHeader);

        this.gridOptions = this.gridFormatterService.BuildDefaultGridOptionsWithColumns(this.createColumnDefs());

        this.loader.toggleLoader(true, "Loading Contact Info...")
        //Fetch all contact stubs as we need to know who all in the org has what agent types
        this.subscriptions.add(this.contactService.getAll<Contact[]>({ organizationId: orgId } as ContactSearchSpec).subscribe(orgContacts => {
            let matchingContactsForDeletion = orgContacts.filter(c => c.id == contactId);
            if (matchingContactsForDeletion.length > 0) {
                this._contact = matchingContactsForDeletion[0];
                //Add Contact button text
                if (this.isCurrentlyPrimaryContact())
                    this.addContactButtonName = "Registration";

                this.contactFullName = this._contact.fullLegalName ? this._contact.fullLegalName : this._contact.displayName;

                //Update the subheader
                this.subHeader = new SubHeader({
                    title: "Delete Contact: " + this.contact.firstName + " " + this.contact.lastName,
                    buttons: [{ name: "Cancel", method: () => this.cancel() }]
                });
                this.subHeaderService.updateSubHeader(this.subHeader);
            }
            //filter out the main contact and any currently disabled contacts from the list
            this._contacts = orgContacts.filter(c => c.id != contactId && c.canLogIn);

            this.loader.toggleLoader(false);

        }, (error) => {
            console.log(error);
            this.loader.toggleLoader(false);
        }));

        //NEED to fetch org type domain and match to org.typeCode
        //Fetch the org stus as we need to know the org's type code
        this.subscriptions.add(this.organizationService.getOrganizationStub(orgId).subscribe(org => {
            this.organization = org;
        }, (error) => {
            console.log(error);
            this.loader.toggleLoader(false);
        }));

        this.agentTypes = AgentTypeOptions.All;

        this.hasAdminManageContactsPermissions = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.ManageContacts).length > 0;
        this.hasLimitedAdminManageContactsPermissions = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.ManageContactsLimited).length > 0;
    }

    ngOnDestroy() {
        if (this.subscriptions) this.subscriptions.unsubscribe();
    }

    public get agentTypeToElevate(): string {
        if (this.isCurrentlyPrimaryContact()) return "Primary Contact";
        if (this.isCurrentlySoleExecutiveAuthorityContact()) return "Executive Authority";
        if (this.isCurrentlyRequiredAndSoleStatutoryAgentContact) return "Statutory Agent";
    }

    public get orgType(): string {
        switch (this.organization.typeCode) {
            case "CORP":
            case "CO": return "Corporation";
            case "LLC":
            case "LC": return "Limited Liability Corporation";
            case "LLP":
            case "LL": return "Limited Liability Partnership";
            case "LP": return "Limited Partnership";
            case "BIZTRUST":
            case "PRSTRUST":
            case "REITRUST":
            case "TR": return "Trust";
            default: return this.organization.typeCode;
        }

    }

    public addContact() {
        //Default it to the add new contact flow
        let flowToStart = "oxuNg1LoxvMUSQAwU";
        //If current contact is primary, addContact kicks off a Path C registration flow instead
        if (this.isCurrentlyPrimaryContact())
            flowToStart = "aaUGI1IntUMYTK1IJd"

        this.router.navigateByUrl('/flow/create/' + flowToStart);
    }

    get contact(): Contact | null {
        return this._contact;
    }


    get contacts(): Contact[] {
        return this._contacts;
    }

    public cancel(): void {
        this.location.back();
    }

    public deleteContact() {
        this.loader.toggleLoader(true);

        this.deleteConfirmDialog.message = "Are you sure you want to delete " + this.contact.firstName + " " + this.contact.lastName + "?";
        this.deleteConfirmDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.deleteConfirmDialog);
    } 

    public edit(obj: Contact): void {
        this.router.navigate([`/admin/contact/${obj.id}`])
    }

    public isCurrentlyPrimaryContact(): boolean {
        return (this.contact.agentType & AgentType.PrimaryContact) !== 0;
    }

    public isCurrentlySoleExecutiveAuthorityContact(): boolean {
        return this.isOnlyContactWithSpecificAgentType(AgentType.ExecutiveAuthority);
    }

    public isCurrentlyRequiredAndSoleStatutoryAgentContact(): boolean {
        return this.orgTypesRequiringAStatutoryAgent.filter(x => x == this.organization.typeCode).length > 0
            && this.isOnlyContactWithSpecificAgentType(AgentType.StatutoryAgent);
    }

    public onDeleteDialogConfirm(): void {
        this.onDeleteDialogClose();

        //Our "Deletion" just strips all agentypes, and toggles their ability to login to false.
        this.contact.agentType = AgentType.None;
        this.contact.canLogIn = false;
        this.subscriptions.add(this.contactService.save(this.contact).subscribe(orgContacts => {
            this.loader.toggleLoader(false);
            this.location.back();

        }, (error) => {
            console.log(error);
            this.loader.toggleLoader(false);
        }));
    }

    public onDeleteDialogClose(): void {
        this.loader.toggleLoader(false);
        this.deleteConfirmDialog.toggleDialog(false);
        this.dialogService.updateDialog(this.deleteConfirmDialog);
    }

    public userHasRole(role: string): boolean {
        const roleNumber: number = parseInt(role);
        let contact = this.contact;
        let value = (contact.agentType & roleNumber) !== 0;
        return value;
    }

    private isOnlyContactWithSpecificAgentType(agentType: AgentType): boolean {
        return (this.contact.agentType & agentType) !== 0
            && this._contacts.filter(c => c.id != this.contact.id && (c.agentType & agentType) !== 0).length <= 0;
    }

    public createColumnDefs(): ColDef[] {
        const defaultColumns: ColDef[] = [
            {
                headerName: "Name",
                headerTooltip: "Name",
                field: "displayName",
                tooltipField: 'displayName',
                filter: "agTextColumnFilter",
                filterParams: this.gridFormatterService.defaultTextFilterParams,
            },
            {
                headerName: "Email",
                headerTooltip: "Email",
                tooltipField: 'emailAddress',
                field: "emailAddress",
                filter: "agTextColumnFilter",
                filterParams: this.gridFormatterService.defaultTextFilterParams,
            },
            {
                headerName: "Phone",
                headerTooltip: "Phone",
                field: "primaryPhone",
                tooltipField: 'primaryPhone',
                filter: "agTextColumnFilter",
                filterParams: this.gridFormatterService.defaultTextFilterParams,
            },
            {
                headerName: "Is User",
                headerTooltip: "Is User",
                field: "isUser",
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "isUser",
                    description: "Is User",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
                tooltipValueGetter: this.gridFormatterService.formatBooleanToYesNo("isUser"),
                filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanfilterParams,
                maxWidth: 120
            },
            {
                headerName: "Elevate Contact",
                field: "",
                cellStyle: { 'text-align': 'right', 'line-height': 1.75 },
                cellRenderer: IconActionRendererComponent,
                cellRendererParams: {
                    buttons: [
                        {
                            action: (obj) => this.edit(obj),
                            label: 'Elevate Contact',
                            icon: 'edit',
                            isVisible: (obj) => true
                        },
                    ]
                },
                maxWidth: 160
            }
        ];

        return defaultColumns;
    }
}


